import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { createLogger } from '@/lib/structuredLogger';

const logger = createLogger('Haptics');

/**
 * Haptic feedback utility using Capacitor for native mobile feel
 * Provides tactile feedback for user interactions
 * Falls back gracefully on web browsers
 */

type HapticType = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

const isNativeCapacitorPlatform = () => Capacitor.isNativePlatform();

/**
 * Trigger haptic feedback with native feel on mobile
 * Falls back to web vibration API if Capacitor is not available
 */
export const triggerHaptic = async (type: HapticType = 'light'): Promise<void> => {
  try {
    if (isNativeCapacitorPlatform()) {
      switch (type) {
        case 'light':
          await Haptics.impact({ style: ImpactStyle.Light });
          break;
        case 'medium':
          await Haptics.impact({ style: ImpactStyle.Medium });
          break;
        case 'heavy':
          await Haptics.impact({ style: ImpactStyle.Heavy });
          break;
        case 'success':
          await Haptics.notification({ type: NotificationType.Success });
          break;
        case 'warning':
          await Haptics.notification({ type: NotificationType.Warning });
          break;
        case 'error':
          await Haptics.notification({ type: NotificationType.Error });
          break;
        default: {
          const unreachableType: never = type;
          return unreachableType;
        }
      }
    } else if (typeof navigator !== 'undefined' && navigator.vibrate) {
      // Fallback to vibration API
      const patterns: Record<HapticType, number | number[]> = {
        light: 10,
        medium: 20,
        heavy: 30,
        success: [10, 50, 10],
        warning: [20, 100, 20],
        error: [30, 100, 30, 100, 30],
      };
      navigator.vibrate(patterns[type]);
    }
  } catch (error) {
    if (!import.meta.env.PROD) {
      logger.warn('Haptic feedback failed', { error });
    }
  }
};

/**
 * Trigger selection haptic (light feedback)
 */
export const hapticSelection = () => triggerHaptic('light');

/**
 * Trigger impact haptic (medium feedback)
 */
export const hapticImpact = () => triggerHaptic('medium');

/**
 * Trigger notification haptic (success pattern)
 */
export const hapticNotification = (type: 'success' | 'warning' | 'error' = 'success') =>
  triggerHaptic(type);
